import { useEffect } from 'react';

/** Keep the app inside the visible browser area, including keyboard panning.
 * Browser pinch zoom changes visualViewport too, but must not relayout the DAW.
 * Normal scrolling and editor zoom remain independent of the browser viewport.
 */
export default function useVisualViewportHeight() {
  useEffect(() => {
    const root = document.documentElement;
    const viewport = window.visualViewport;
    const properties = ['--cu-app-height', '--cu-app-top'];
    const previous = properties.map(name => root.style.getPropertyValue(name));

    const apply = () => {
      // Pinching shrinks the visible CSS rectangle without shrinking the app.
      // Reflowing here used to compound zoom and displace the bottom controls.
      if (viewport && Math.abs((viewport.scale || 1) - 1) > 0.01) return;
      const height = viewport ? viewport.height : window.innerHeight;
      const top = viewport ? Math.max(0, viewport.offsetTop || 0) : 0;
      if (!(height > 0)) return;
      [height, top].forEach((value, index) => {
        const next = `${Math.round(value)}px`;
        if (root.style.getPropertyValue(properties[index]) !== next) {
          root.style.setProperty(properties[index], next);
        }
      });
    };

    apply();
    window.addEventListener('resize', apply);
    window.addEventListener('orientationchange', apply);
    viewport?.addEventListener('resize', apply);
    viewport?.addEventListener('scroll', apply);

    return () => {
      window.removeEventListener('resize', apply);
      window.removeEventListener('orientationchange', apply);
      viewport?.removeEventListener('resize', apply);
      viewport?.removeEventListener('scroll', apply);
      properties.forEach((name, index) => {
        if (previous[index]) root.style.setProperty(name, previous[index]);
        else root.style.removeProperty(name);
      });
    };
  }, []);
}
